import { type ApolloError, gql, useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import type { Transaction } from "@/apis/gql/types";
import { useTenant } from "@/providers/session";

import { GET_ALL_TRANSACTIONS } from "./get-all";

type ApproveTransactionBookingInput = {
  bankTransactionId: string;
};

export const useApproveTransactionBooking = (): [
  (input: ApproveTransactionBookingInput) => Promise<Transaction[]>,
  {
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const { tenantId } = useTenant();

  const [approveTransactionBooking, { loading, error }] = useMutation<{
    approveTransactionBooking: Transaction[];
  }>(APPROVE_TRANSACTION_BOOKING_MUTATION, {
    refetchQueries: [GET_ALL_TRANSACTIONS],
    awaitRefetchQueries: true,
    update: (cache, { data }) => {
      if (!data?.approveTransactionBooking) return;

      cache.evict({ fieldName: "transactions" });
      cache.gc();
    },
  });

  const handleApproveTransactionBooking = async (
    input: ApproveTransactionBookingInput,
  ) => {
    if (!tenantId) {
      throw new Error("Tenant ID is required");
    }

    if (!input.bankTransactionId) {
      throw new Error("Bank transaction ID is required");
    }

    const toastId = toast.loading(t("generic_loading"));

    try {
      const { data: response } = await approveTransactionBooking({
        variables: {
          bankTransactionId: input.bankTransactionId,
        },
      });

      if (!response) {
        throw new Error("No response from approveTransactionBooking");
      }

      toast.dismiss(toastId);

      return response.approveTransactionBooking;
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      throw error;
    }
  };

  return [handleApproveTransactionBooking, { loading, error }];
};

export const APPROVE_TRANSACTION_BOOKING_MUTATION = gql`
  mutation ApproveTransactionBooking(
    $bankTransactionId: String!
  ) {
    approveTransactionBooking(
      bankTransactionId: $bankTransactionId
    ) {
      bankTxnId
    }
  }
`;
